import { useEffect, useRef } from 'react';
import ReactPlayer, { ReactPlayerProps } from 'react-player';

import { makeStyles } from 'tss-react/mui';

import { CAROUSEL_HEIGHT, IMAGE_HEIGHT } from '@/constants/mediaDisplay';

interface VideoProps {
  id?: string;
  lastClick?: string | number;
  onReady?: () => void;
  onPlay?: () => void;
  onProgress?: ReactPlayerProps['onProgress'];
  platform?: string;
  playing?: boolean;
  seek?: number;
  url: string;
  isCarousel?: boolean;
}

const useStyles = makeStyles()(() => ({
  defaultWrapper: {
    position: 'relative'
  }
}));

const VideoPlayer = ({
  lastClick = new Date().toTimeString(),
  onReady,
  onPlay,
  onProgress,
  playing = false,
  seek,
  url,
  isCarousel = false
}: VideoProps) => {
  const videoRef = useRef<ReactPlayer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const styles = useStyles();

  const postToYT = (
    iframe: HTMLIFrameElement,
    func: string,
    args: unknown[] = []
  ) => {
    iframe.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func, args }),
      '*'
    );
  };

  useEffect(() => {
    if (!videoRef.current) return;

    const internalPlayer = videoRef.current.getInternalPlayer();
    const iframe = internalPlayer?.getIframe?.();
    const isYouTube = internalPlayer?.options?.host.includes('youtube');

    if (!isYouTube || !iframe?.contentWindow) {
      videoRef.current.seekTo(seek || 0, 'seconds');
      return;
    }

    const playerState = internalPlayer?.getPlayerState?.();
    const send = (func: string, args: unknown[] = []) =>
      postToYT(iframe, func, args);

    if (playerState === 5) {
      send('mute');
      send('playVideo');
      send('seekTo', [seek || 0, true]);
      setTimeout(() => {
        send('pauseVideo');
        send('unMute');
      }, 1000);
    } else {
      send('seekTo', [seek || 0, true]);
    }
  }, [lastClick]);

  // This function will be called after the video is ready
  const handleReady = () => {
    if (onReady) onReady();

    // Set a data attribute on the video element that we can use for calculations
    setTimeout(() => {
      const videoElement = containerRef.current?.querySelector('video');
      if (videoElement && videoElement.videoWidth && videoElement.videoHeight) {
        const aspectRatio = videoElement.videoWidth / videoElement.videoHeight;
        containerRef.current?.setAttribute(
          'data-video-aspect',
          aspectRatio.toString()
        );
      }
    }, 500); // Small delay to ensure video metadata is loaded
  };

  return (
    <div
      className={styles.classes.defaultWrapper}
      id="video-container"
      ref={containerRef}
    >
      <ReactPlayer
        onReady={handleReady}
        onPlay={onPlay}
        onProgress={onProgress}
        progressInterval={10}
        playing={playing}
        controls={true}
        url={url}
        ref={videoRef}
        height={isCarousel ? CAROUSEL_HEIGHT : IMAGE_HEIGHT}
        width="100%"
        className="react-player"
      />
    </div>
  );
};

export default VideoPlayer;
